import type { Feature } from 'geojson';
import { landmarkImageRevision } from './landmark-markers.js';
import type { Map as GlobeMap } from 'maplibre-gl';
import type { Stroke } from '../types/app.js';
import { createMapLibreProjection } from './globe-projection.js';
import { GlobeSource, globeFeature } from './globe-source.js';
import { globeStrokePath } from './globe-geometry.js';
import { createGlobeTextBillboard, createGlobeMarkerLabel } from './globe-labels.js';
import { drawMapMarker, getMarkerScreenSize } from './marker-renderer.js';
import { getRenderedWidth, getArrowHeadLength } from './map-math.js';
import { getStrokeOpacity, getPolygonFillOpacity, getPolygonOutlineOpacity } from './stroke-style.js';

export class GlobeStrokes {
  private readonly source: GlobeSource;
  private readonly signatures = new Map<string, { key: string; points: Stroke['points']; controls: Stroke['curveControls'] }>();
  private readonly featureIds = new Map<string, string[]>();
  constructor(private readonly map: GlobeMap) { this.source = new GlobeSource(map, 'drawings'); }
  sync(strokes: Stroke[], erased: Set<string> | null): void {
    const desired = new Set(strokes.map(stroke => stroke.id));
    const remove: string[] = [];
    const add: Feature[] = [];
    for (const id of this.signatures.keys()) if (!desired.has(id)) {
      remove.push(...this.featureIds.get(id) ?? []);
      this.signatures.delete(id); this.featureIds.delete(id);
      this.source.removeImage(id); this.source.removeImage(`${id}-label`);
    }
    for (const stroke of strokes) {
      const zoom = this.map.getZoom() + 1;
      const alpha = erased?.has(stroke.id) ? 0.25 : 1;
      const { points, curveControls, ...style } = stroke;
      const key = JSON.stringify([style, alpha, landmarkImageRevision(), stroke.zoomScale !== false ? zoom : 0, stroke.tool === 'arrow' ? [this.map.getCenter().lng, this.map.getCenter().lat, this.map.getBearing(), this.map.getPitch(), zoom] : null]);
      const previous = this.signatures.get(stroke.id);
      if (previous?.key === key && previous.points === points && previous.controls === curveControls) continue;
      remove.push(...this.featureIds.get(stroke.id) ?? []);
      const features = this.features(stroke, zoom, alpha);
      add.push(...features);
      this.featureIds.set(stroke.id, features.map(feature => String(feature.id)));
      this.signatures.set(stroke.id, { key, points, controls: curveControls });
    }
    this.source.patch(remove, add);
  }
  private features(stroke: Stroke, zoom: number, alpha: number): Feature[] {
    const id = stroke.id;
    const point = { type: 'Point' as const, coordinates: [stroke.points[0].lng, stroke.points[0].lat] };
    const color = stroke.color || '#5b4bff';
    const opacity = alpha * getStrokeOpacity(stroke);
    const width = getRenderedWidth(stroke, zoom);
    if (stroke.tool === 'text') {
      const label = createGlobeTextBillboard(stroke, alpha, zoom);
      return label ? [globeFeature(id, point, { image: this.source.image(id, label.image, label.pixelRatio), offset: label.offset, opacity: label.opacity })] : [];
    }
    if (stroke.tool === 'marker') {
      const size = getMarkerScreenSize(width, stroke.markerIcon);
      const image = document.createElement('canvas');
      const pixelRatio = Math.min(2, 4096 / Math.max(size.width, size.height));
      image.width = Math.max(1, Math.ceil(size.width * pixelRatio)); image.height = Math.max(1, Math.ceil(size.height * pixelRatio));
      const context = image.getContext('2d')!;
      context.scale(pixelRatio, pixelRatio);
      const pin = !stroke.markerIcon || stroke.markerIcon === 'map-pin';
      drawMapMarker(context, { x: size.width / 2, y: pin ? size.height : size.height / 2 }, color, width, stroke.markerIcon);
      const features = [globeFeature(id, point, { image: this.source.image(id, image, pixelRatio), anchor: pin ? 'bottom' : 'center', opacity })];
      const label = createGlobeMarkerLabel(stroke, alpha, zoom);
      if (label) features.push(globeFeature(`${id}-label`, point, { image: this.source.image(`${id}-label`, label.image, label.pixelRatio), offset: label.offset, anchor: 'top', opacity: label.opacity }));
      return features;
    }
    if (stroke.points.length < 2) return [];
    const coordinates = globeStrokePath(stroke);
    const features = [globeFeature(id, { type: 'LineString', coordinates }, { color, width, opacity: stroke.tool === 'polygon' ? alpha * getPolygonOutlineOpacity(stroke) : opacity, dashed: stroke.lineStyle === 'dashed' })];
    if (stroke.tool === 'polygon') features.unshift(globeFeature(`${id}-fill`, { type: 'Polygon', coordinates: [coordinates] }, { fill: stroke.fillColor || color, fillOpacity: alpha * getPolygonFillOpacity(stroke) }));
    if (stroke.tool === 'arrow') {
      const tip = createMapLibreProjection(this.map).project({ lng: coordinates[coordinates.length - 1][0], lat: coordinates[coordinates.length - 1][1] });
      if (!tip) return features;
      const previous = this.map.project({ lng: coordinates[coordinates.length - 2][0], lat: coordinates[coordinates.length - 2][1] });
      const angle = Math.atan2(tip.y - previous.y, tip.x - previous.x);
      const length = getArrowHeadLength(width);
      const wings = [-Math.PI / 6, Math.PI / 6].map(offset => {
        const p = this.map.unproject([tip.x - length * Math.cos(angle + offset), tip.y - length * Math.sin(angle + offset)]);
        return [p.lng, p.lat];
      });
      features.push(globeFeature(`${id}-arrow`, { type: 'LineString', coordinates: [wings[0], coordinates[coordinates.length - 1], wings[1]] }, { color, width, opacity }));
    }
    return features;
  }
}
